import { BRAND_CLASSES } from '@/constants/theme'
import { CharCounter } from './CharCounter'
import { getFieldLabelClasses, getInputSizing, type LabelSize, type ControlDensity } from './formLabelUtils'

export type TextBoxInputType = 'text' | 'email' | 'url' | 'tel' | 'password' | 'search'

export interface TextBoxProps {
  /** Field name — also used as the input id for label `htmlFor`. */
  name: string
  /** Current value (controlled). */
  value: string
  /** Called with the new string value whenever the user types. */
  onChange: (next: string) => void
  /** Optional blur handler (no event arg — matches RHF's field.onBlur). */
  onBlur?: () => void

  /** Field label */
  label?: string
  /** Whether field is required */
  required?: boolean
  /** Error message to display */
  error?: string
  /** Helper text to display below input */
  helperText?: string
  /** Whether to render as textarea */
  multiline?: boolean
  /** Number of rows for textarea (default: 3) */
  rows?: number
  /** Show character counter */
  showCharCounter?: boolean
  /** Maximum character length for counter */
  maxLength?: number
  /**
   * When true (default), applies `maxLength` as a hard browser cap (input is truncated).
   * Set false to keep the counter/validation against `maxLength` while allowing the user to
   * exceed it (e.g. so an oversized paste isn't silently truncated and can be surfaced/blocked).
   */
  enforceMaxLength?: boolean
  /** Whether counter warning threshold is reached (default: 0.8) */
  warnAt?: number
  /** Custom class name for container */
  containerClassName?: string
  /** Label size variant */
  labelSize?: LabelSize
  /** Field density: 'compact' tightens padding/radius/text for panel-style forms. */
  density?: ControlDensity
  /** Custom class name for label */
  labelClassName?: string
  /** Placeholder text */
  placeholder?: string
  /** Custom class name for input/textarea */
  className?: string

  /** HTML input type (only meaningful when multiline is false) */
  type?: TextBoxInputType
  /** Disable the field */
  disabled?: boolean
  /** Auto-focus the field on mount */
  autoFocus?: boolean
  /**
   * The browser's autofill hint, e.g. "off" on an email field meant for someone else's address, where the
   * `email` token would offer the signed-in user's own.
   */
  autoComplete?: string
  /** Keydown handler (e.g. Enter / Escape on inline editors) */
  onKeyDown?: (e: KeyboardEvent) => void
}

/**
 * Pure controlled text input. Always requires `value` and `onChange`. For
 * react-hook-form integration use `TextBoxField` from `@/components/fields/TextBoxField`,
 * which wraps this component in a `<Controller>`.
 */
export function TextBox({
  name,
  value,
  onChange,
  onBlur,
  label,
  required = false,
  error,
  helperText,
  multiline = false,
  rows = 3,
  showCharCounter = false,
  maxLength,
  enforceMaxLength = true,
  warnAt = 0.8,
  className = '',
  containerClassName = '',
  labelSize = 'sm',
  density = 'comfortable',
  labelClassName = '',
  placeholder,
  type = 'text',
  disabled = false,
  autoFocus = false,
  autoComplete,
  onKeyDown,
}: TextBoxProps) {
  const hasError = !!error
  // The message under the field is read out with it, so an error shown on screen is heard too.
  const errorId = `${name}-error`
  const helperId = `${name}-helper`
  const describedBy = hasError ? errorId : helperText ? helperId : undefined
  const inputClasses = `w-full border ${getInputSizing(density)} focus:outline-none focus:ring-2 focus:border-transparent ${
    hasError
      ? `${BRAND_CLASSES.INPUT.ERROR}`
      : `${BRAND_CLASSES.INPUT.NORMAL} ${BRAND_CLASSES.FOCUS.PRIMARY}`
  } ${BRAND_CLASSES.INPUT.PLACEHOLDER} ${multiline ? 'resize-y' : ''} ${className}`

  // Sync on both `input` (per-keystroke typing) and `change`. Browser autofill /
  // autocomplete selections fire `change` but not always `input`; without the `change`
  // listener a controlled field can display an autofilled value while the form state
  // stays empty (e.g. a picked LinkedIn URL that still validates as "required").
  const handleInput = (e: Event) => {
    const target = e.currentTarget as HTMLInputElement | HTMLTextAreaElement
    onChange(target.value)
  }

  return (
    <div className={containerClassName}>
      {label && (
        <label
          htmlFor={name}
          className={getFieldLabelClasses(density, labelSize, labelClassName)}
        >
          {label}
          {required && <span className={`${BRAND_CLASSES.INPUT.REQUIRED} ml-1`}>*</span>}
        </label>
      )}

      {multiline ? (
        <textarea
          id={name}
          name={name}
          rows={rows}
          maxLength={enforceMaxLength ? maxLength : undefined}
          placeholder={placeholder}
          value={value}
          onInput={handleInput}
          onChange={handleInput}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          aria-invalid={hasError || undefined}
          aria-describedby={describedBy}
          className={inputClasses}
        />
      ) : (
        <input
          id={name}
          name={name}
          type={type}
          maxLength={enforceMaxLength ? maxLength : undefined}
          placeholder={placeholder}
          value={value}
          onInput={handleInput}
          onChange={handleInput}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          aria-invalid={hasError || undefined}
          aria-describedby={describedBy}
          className={inputClasses}
        />
      )}

      {showCharCounter && maxLength && (
        <CharCounter value={value} max={maxLength} warnAt={warnAt} />
      )}

      {error && (
        <p id={errorId} className={`mt-1 text-sm ${BRAND_CLASSES.INPUT.ERROR_TEXT}`}>{error}</p>
      )}

      {helperText && !error && (
        <p id={helperId} className={`mt-1 text-xs ${BRAND_CLASSES.INPUT.HELPER}`}>{helperText}</p>
      )}
    </div>
  )
}
